import { useMutation } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { SequenceMenu } from '@sequencehq/core-components'
import { useNotifications } from '@sequencehq/core-components'
import { usePermissions } from 'lib/rbac'
import invariant from 'tiny-invariant'

const useDuplicateQuote = () => {
  const { displayPromiseNotification } = useNotifications()

  const duplicationMutation = useMutation(
    dashboard20240730Client.postQuoteDuplicate
  )

  const duplicateQuote = async (quoteId: string): Promise<string | null> => {
    try {
      const duplicatedQuote = await displayPromiseNotification(
        duplicationMutation.mutateAsync({
          id: quoteId
        }),
        {
          loading: 'Duplicating quote...',
          success: 'Quote duplicated',
          error: 'Unable to duplicate quote'
        }
      )

      invariant(duplicatedQuote, 'Quote not found')
      return duplicatedQuote.id
    } catch {
      return null
    }
  }

  return {
    duplicateQuote,
    inProgress: duplicationMutation.isPending
  }
}

export const DuplicateQuoteMenuItem = (props: {
  quoteId: string
  disabled?: boolean
  disabledReason?: string
  onSuccess?: (newQuoteId: string) => void
}) => {
  const { duplicateQuote } = useDuplicateQuote()
  const { can } = usePermissions()

  if (!can('org.quote:create')) {
    return null
  }

  return (
    <SequenceMenu.Item
      uuid="duplicate-quote"
      label="Duplicate quote"
      data-testid={`quotes.${props.quoteId}.menuItem.duplicate`}
      isDisabled={props.disabled}
      tooltip={props.disabledReason}
      onClick={async () => {
        const res = await duplicateQuote(props.quoteId)
        if (res) {
          props.onSuccess?.(res)
        }
      }}
    />
  )
}
